import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { format, addDays } from "date-fns";
import { tr, enGB } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import io from "socket.io-client";
import {
  Calendar, Clock, Phone, Edit, Trash2, Check, X,
  MoreVertical, User, LayoutDashboard, Sun, Moon, FileText, Coffee, TrendingUp, CalendarDays, ChevronDown, Layers, Plus, Share
} from "lucide-react";
import { toast } from "sonner";
import api from "../api/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import SessionBadge from "@/components/ui/session-badge";
import { SHOW_APPOINTMENT_CARD_STATUS } from "@/constants/uiFlags";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// --- TUR REHBERİ IMPORT ---
import TourGuide from "../components/TourGuide"; 
// --------------------------
import SwipeableAppointmentCard from "./SwipeableAppointmentCard";

/** Dashboard selamlama: "Fatih Senyüz" → "Fatih" */
function firstNameOnly(fullName) {
  const trimmed = String(fullName || "").trim();
  if (!trimmed) return "";
  return trimmed.split(/\s+/)[0];
}

// --- SEANS PLANLAYICI ---
import SessionPlannerDialog from "./SessionPlannerDialog";

// --- SCROLL-SAFE DROPDOWN ---
// Mobilde kullanıcı parmağıyla listeyi kaydırırken 3-nokta butonuna
// yanlışlıkla değdiğinde Radix DropdownMenu'nun varsayılan
// `onPointerDown -> open` davranışı scroll'u keser ve menüyü açardı.
// Bu wrapper:
//   1) Trigger'ın `onPointerDown`'ında preventDefault ederek Radix'in
//      pointerdown-tabanlı açılış handler'ını iptal eder (Radix, child'ın
//      event.defaultPrevented durumunda kendi handler'ını atlar).
//   2) Open state'ini controlled yönetir.
//   3) Pointer hareketi 8px eşiğini aşarsa kullanıcının niyetini "scroll"
//      olarak kabul eder; sonraki `click` event'i menüyü açmaz.
// Child olarak Radix'e forward edilecek tek bir <button>/<Button> elemanı
// beklenir; ScrollSafeDropdown event handler'larını cloneElement ile inject
// eder.
function ScrollSafeDropdown({ trigger, children, align = "end", contentClassName = "w-48" }) {
  const [open, setOpen] = useState(false);
  const startRef = useRef(null);
  const movedRef = useRef(false);

  const handlePointerDown = (e) => {
    startRef.current = { x: e.clientX, y: e.clientY };
    movedRef.current = false;
    // Radix'in pointerdown-açılış handler'ı bu satır sayesinde devre dışı kalır.
    e.preventDefault();
    trigger.props.onPointerDown?.(e);
  };

  const handlePointerMove = (e) => {
    if (!startRef.current) return;
    const dx = Math.abs(e.clientX - startRef.current.x);
    const dy = Math.abs(e.clientY - startRef.current.y);
    if (dx > 8 || dy > 8) movedRef.current = true;
  };

  const handlePointerEnd = (e) => {
    startRef.current = null;
    trigger.props.onPointerUp?.(e);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (movedRef.current) {
      // Parmak kaymış; scroll niyetiyle açılmamalı.
      movedRef.current = false;
      return;
    }
    trigger.props.onClick?.(e);
    setOpen((v) => !v);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        {React.cloneElement(trigger, {
          onPointerDown: handlePointerDown,
          onPointerMove: handlePointerMove,
          onPointerUp: handlePointerEnd,
          onPointerCancel: handlePointerEnd,
          onPointerLeave: handlePointerEnd,
          onClick: handleClick,
        })}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className={contentClassName}>
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// --- WHATSAPP ICON ---
const WhatsAppIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91C2.13 13.66 2.59 15.36 3.45 16.86L2.05 22L7.3 20.62C8.75 21.41 10.38 21.83 12.04 21.83C17.5 21.83 21.95 17.38 21.95 11.92C21.95 9.27 20.92 6.78 19.05 4.91C17.18 3.03 14.69 2 12.04 2ZM12.05 20.16C10.56 20.16 9.11 19.76 7.83 19L7.51 18.81L4.4 19.62L5.23 16.58L5.02 16.25C4.21 14.96 3.79 13.45 3.8 11.91C3.8 7.37 7.5 3.67 12.05 3.67C14.25 3.67 16.31 4.53 17.87 6.09C19.42 7.65 20.28 9.72 20.28 11.92C20.28 16.46 16.58 20.16 12.05 20.16ZM16.61 14.39C16.36 14.27 15.14 13.67 14.91 13.59C14.68 13.5 14.51 13.46 14.34 13.71C14.18 13.96 13.71 14.5 13.54 14.71C13.38 14.92 13.21 14.96 12.96 14.83C12.71 14.71 11.9 14.44 10.94 13.59C10.19 12.92 9.68 12.1 9.43 11.67C9.18 11.42 9.41 11.19 9.53 11.07C9.64 10.96 9.77 10.8 9.89 10.65C10.01 10.5 10.06 10.39 10.14 10.23C10.22 10.06 10.18 9.92 10.12 9.8C10.06 9.67 9.63 8.63 9.46 8.21C9.28 7.79 9.11 7.85 8.96 7.85C8.81 7.85 8.64 7.84 8.46 7.84C8.28 7.84 7.99 7.91 7.74 8.18C7.49 8.45 6.79 9.11 6.79 10.44C6.79 11.77 7.76 13.06 7.9 13.25C8.04 13.44 9.82 16.19 12.55 17.36C13.2 17.64 13.71 17.81 14.11 17.93C14.76 18.14 15.36 18.11 15.83 18.04C16.36 17.96 17.45 17.38 17.68 16.73C17.91 16.08 17.91 15.52 17.84 15.39C17.78 15.27 17.61 15.19 16.36 14.57L16.61 14.39Z" />
  </svg>
);

const Dashboard = ({ appointments, stats, userRole, onEditAppointment, onNewAppointment, onRefresh, onNavigate, onOpenChat, activationState, ahaOverlayActive = false, forceStartTour, onForceStartTourConsumed }) => {
  const { token, canViewAll } = useAuth();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'tr' ? tr : enGB;
  // State
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [settings, setSettings] = useState(null);
  const [staffMembers, setStaffMembers] = useState([]);
  /** Admin + staff — kartlarda isim çözümlemesi için (settings'ten bağımsız yüklenir) */
  const [staffDirectory, setStaffDirectory] = useState([]);
  const [currentStaffUsername, setCurrentStaffUsername] = useState(null);
  const [currentUserName, setCurrentUserName] = useState("");
  const [personnelStats, setPersonnelStats] = useState(null);
  const [staffFilter, setStaffFilter] = useState("all");
  const socketRef = useRef(null);
  const [expandedNoteId, setExpandedNoteId] = useState(null);
  const [upcomingVisibleCount, setUpcomingVisibleCount] = useState(10);
  // Swipe: aynı anda yalnızca bir kart açık tutulur (lift edilmiş state)
  const [openSwipeCardId, setOpenSwipeCardId] = useState(null);
  const [openSwipeCardSide, setOpenSwipeCardSide] = useState(null);
  // Session Planner States
  const [showSessionPlanner, setShowSessionPlanner] = useState(false);
  const [sessionPlannerAppointment, setSessionPlannerAppointment] = useState(null);
  // Cancel + Refund Dialog
  const [cancelRefundDialog, setCancelRefundDialog] = useState(null);
  const [cancellingRefund, setCancellingRefund] = useState(false);

  // Break States
  const [todayBreaks, setTodayBreaks] = useState([]);
  const [showBreakDialog, setShowBreakDialog] = useState(false);
  const [newBreakStart, setNewBreakStart] = useState("12:00");
  const [newBreakEnd, setNewBreakEnd] = useState("12:30");
  const [addingBreak, setAddingBreak] = useState(false);

  // --- TOUR GUIDE STATE ---
  const [runTour, setRunTour] = useState(false);
  // Tur bu mount'ta yalnızca bir kez planlansın: forceStartTour consumed olunca
  // effect tekrar çalışıp localStorage yolundan ikinci kez tetiklemesin.
  const tourScheduledRef = useRef(false);

  const dashboardTourStorageKey = useMemo(() => {
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const username = payload.sub || payload.username || 'unknown';
      const role = payload.role || userRole || 'unknown';
      return `dashboard_tour_completed:${role}:${username}`;
    } catch (e) {
      return null;
    }
  }, [token, userRole]);

  const today = format(new Date(), "yyyy-MM-dd");
  const tomorrow = format(addDays(new Date(), 1), "yyyy-MM-dd");

  // Saati gelen bekleyen randevuların çizgisi sayfa yenilenmeden nefes almaya başlasın
  const [nowMinute, setNowMinute] = useState(() => format(new Date(), "yyyy-MM-dd HH:mm"));
  useEffect(() => {
    const id = setInterval(() => setNowMinute(format(new Date(), "yyyy-MM-dd HH:mm")), 15000);
    return () => clearInterval(id);
  }, []);

  // --- HELPERS ---
  const calculateEndTime = (startTime, duration) => {
    if (!startTime || !duration) return null;
    try {
      const [hours, minutes] = startTime.split(':').map(Number);
      const totalMinutes = hours * 60 + minutes + parseInt(duration, 10);
      const endHours = Math.floor(totalMinutes / 60);
      const endMinutes = totalMinutes % 60;
      return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
    } catch (e) { return null; }
  };

  // Çoklu hizmette kompakt etiket: "Saç (+2)" — uzun birleşik isim kesilmesin
  const isMultiService = (apt) => Array.isArray(apt?.services) && apt.services.length > 1;

  const serviceDisplayName = (apt) => {
    if (isMultiService(apt)) {
      const sorted = [...apt.services].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
      // İlk hizmet adı; çoklu hizmet sayısı ayrı rozette gösterilir
      return sorted[0]?.name_snapshot || apt.service_name;
    }
    return apt?.service_name;
  };

  // Çoklu hizmet detay listesi (sıra + süre) — kart genişletilince personel hangi
  // hizmetleri sırayla vereceğini görür
  const renderServiceBreakdown = (apt) => {
    if (!isMultiService(apt)) return null;
    const sorted = [...apt.services].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
    return (
      <div className="space-y-1.5">
        {sorted.map((s, idx) => (
          <div key={s.service_id || idx} className="flex items-start justify-between gap-2 text-sm">
            <div className="flex items-start gap-2 min-w-0 flex-1">
              <span className="w-5 h-5 mt-0.5 flex-shrink-0 rounded-full bg-zinc-900 text-white text-[11px] font-bold flex items-center justify-center">{idx + 1}</span>
              <span className="font-medium text-gray-800 break-words leading-relaxed min-w-0">{s.name_snapshot}</span>
            </div>
            {s.duration_snapshot ? (
              <span className="flex-shrink-0 mt-0.5 text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{s.duration_snapshot} {t('publicBooking.minutes')}</span>
            ) : null}
          </div>
        ))}
      </div>
    );
  };

  const toggleNote = (id) => setExpandedNoteId(prev => prev === id ? null : id);

  // Dinamik Selamlama + Emoji
  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return { text: t('dashboard.greetings.morning'), emoji: "☀️" };
    if (hour >= 12 && hour < 18) return { text: t('dashboard.greetings.noon'), emoji: "🌤️" };
    if (hour >= 18 && hour < 22) return { text: t('dashboard.greetings.evening'), emoji: "🌙" };
    return { text: t('dashboard.greetings.night'), emoji: "✨" };
  };

  // --- LOADERS ---
  const loadPersonnelStats = useCallback(async () => {
    try { const res = await api.get("/stats/personnel"); setPersonnelStats(res.data); } catch (e) { console.error(e); }
  }, []);

  const loadSettings = async () => {
    try { const res = await api.get("/settings"); setSettings(res.data); } catch (e) { console.error(e); }
  };

  const loadTodayBreaks = async () => {
    try { const res = await api.get(`/staff/breaks?date=${today}`); setTodayBreaks(res.data?.breaks || []); } catch (e) { console.error(e); }
  };

  const loadStaffMembers = async () => {
    if (userRole !== 'admin' && !canViewAll) return;
    try {
      const res = await api.get("/users");
      let staff = (res.data || []).filter(u => u.role === 'staff');
      if (settings?.admin_provides_service !== false) {
        const admins = (res.data || []).filter(u => u.role === 'admin');
        staff = [...staff, ...admins];
      }
      setStaffMembers(staff);
    } catch (e) { console.error(e); }
  };

  // Kart üzerinde isim çözümlemesi için TÜM personel + admin'i settings'ten
  // bağımsız yükler. Böylece isim "bi gidip bi geliyor" sorunu olmaz:
  // staffMembers (filtre çipleri) settings'e bağlı geç gelir + admin'i
  // admin_provides_service=false ise hiç içermez → isim kaybolurdu.
  const loadStaffDirectory = async () => {
    if (userRole !== 'admin' && !canViewAll) return;
    try {
      const res = await api.get("/users");
      setStaffDirectory((res.data || []).filter(u => u.role === 'staff' || u.role === 'admin'));
    } catch (e) { console.error(e); }
  };

  const loadCurrentStaffUsername = async () => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const username = payload.sub || payload.username;
        const fullName = payload.full_name || payload.name;
        setCurrentStaffUsername(username);
        setCurrentUserName(firstNameOnly(fullName) || username || t('dashboard.userFallback'));
      } catch (e) { console.error(e); }
    }
  };

  // --- EFFECTS ---
  useEffect(() => {
    loadSettings();
    loadTodayBreaks();
    loadCurrentStaffUsername();
    if (userRole === 'staff' && !canViewAll) {
      loadPersonnelStats();
    }
    
    // --- TUR KONTROLÜ (LOCAL STORAGE + AHA STATE GATING) ---
    // Aha akışı admin için DEFINITIVE terminal state'e ulaştığında turu açabiliriz.
    // 'pending_aha_appointment' veya 'aha_wa_failed' iken App.js Aha overlay'ini gösterir,
    // tur ekrana çıkmaz (overlay zaten ekranı yönetir).
    // Önemli: activationState undefined (currentUser henüz yüklenmedi) iken
    // turu başlatmıyoruz — aksi halde Aha spotlight ile tur AYNI ANDA açılabilir.
    const ahaTerminalForTour =
      activationState === 'aha_completed' ||
      activationState === 'aha_skipped' ||
      activationState === 'skip_aha_no_phone' ||
      activationState === 'not_applicable';

    // Staff turu görmemeli (sadece admin için) + Aha state definitive terminal olmalı
    const tourEligible = userRole === 'admin' && ahaTerminalForTour;

    // ahaOverlayActive: Aha spotlight/celebration/error overlay ekrandayken
    // turu başlatmıyoruz — yoksa welcome dialog Aha celebration üstüne biner.
    if (ahaOverlayActive) return;

    // Tur bu mount'ta zaten planlandıysa tekrar planlama (çift render/çift tur önlemi)
    if (tourScheduledRef.current) return;

    // forceStartTour=true → localStorage flag'ine bakmadan turu zorla başlat (Aha akışı sonrası)
    if (forceStartTour && tourEligible) {
      tourScheduledRef.current = true;
      setTimeout(() => setRunTour(true), 400);
      if (onForceStartTourConsumed) onForceStartTourConsumed();
      return;
    }

    if (
      tourEligible &&
      dashboardTourStorageKey &&
      !localStorage.getItem(dashboardTourStorageKey)
    ) {
      // Sayfa tam yüklensin diye azıcık beklet
      tourScheduledRef.current = true;
      setTimeout(() => setRunTour(true), 1000);
    }
  }, [userRole, loadPersonnelStats, dashboardTourStorageKey, activationState, forceStartTour, onForceStartTourConsumed, ahaOverlayActive]);

  useEffect(() => { if (settings !== null) loadStaffMembers(); }, [settings, userRole]);
  useEffect(() => { loadStaffDirectory(); /* eslint-disable-next-line */ }, [userRole, canViewAll]);
  useEffect(() => { if (userRole === 'staff' && !canViewAll) loadPersonnelStats(); }, [appointments.length, userRole, canViewAll, loadPersonnelStats]);

  useEffect(() => {
    if (!socketRef.current && token) {
      const socketUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const socket = io(socketUrl, { path: '/api/socket.io', transports: ['websocket', 'polling'], auth: { token: token } });
      socketRef.current = socket;
      socket.on('connect', () => { try { const p = JSON.parse(atob(token.split('.')[1])); if (p.org_id) socket.emit('join_organization', { organization_id: p.org_id }); } catch (e) {} });
      socket.on('appointment_created', () => { if (onRefresh) onRefresh(); if (userRole === 'staff' && !canViewAll) loadPersonnelStats(); });
      socket.on('appointment_updated', () => { if (onRefresh) onRefresh(); if (userRole === 'staff' && !canViewAll) loadPersonnelStats(); });
      socket.on('appointment_deleted', () => { if (onRefresh) onRefresh(); if (userRole === 'staff' && !canViewAll) loadPersonnelStats(); });
      socket.on('appointments_bulk_updated', () => { if (onRefresh) onRefresh(); if (userRole === 'staff' && !canViewAll) loadPersonnelStats(); });
    }
    return () => { if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; } };
  }, [token, onRefresh, userRole, loadPersonnelStats]);

  // --- HANDLERS ---
  const handleStatusChange = async (id, status) => { try { await api.put(`/appointments/${id}`, { status }); toast.success(t('dashboard.toast.statusUpdated')); onRefresh(); } catch (e) { toast.error(t('dashboard.toast.statusError')); } };
  const handleDelete = async (id) => { try { await api.delete(`/appointments/${id}`); toast.success(t('dashboard.toast.appointmentDeleted')); setDeleteDialog(null); onRefresh(); } catch (e) { toast.error(t('dashboard.toast.deleteError')); } };
  const handleAddBreak = async () => { if (!newBreakStart || !newBreakEnd) return; setAddingBreak(true); try { await api.post("/staff/breaks", { date: today, start_time: newBreakStart, end_time: newBreakEnd }); setShowBreakDialog(false); loadTodayBreaks(); } catch (e) { toast.error(e.response?.data?.detail); } finally { setAddingBreak(false); } };
  const handleDeleteBreak = async (id) => { try { await api.delete(`/staff/breaks/${id}`); toast.success(t('dashboard.breaks.breakDeleted')); loadTodayBreaks(); } catch (e) { toast.error(t('dashboard.breaks.deleteError')); } };
  const handleCancelOnly = async (id) => {
    setCancellingRefund(true);
    try {
      await api.put(`/appointments/${id}`, { status: t('dashboard.status.cancelled') });
      toast.success(i18n.language === 'tr' ? 'Randevu iptal edildi' : 'Appointment cancelled');
      setCancelRefundDialog(null); onRefresh();
    } catch (e) { toast.error(i18n.language === 'tr' ? 'İptal başarısız' : 'Cancel failed'); }
    finally { setCancellingRefund(false); }
  };
  const handleCancelAndRefund = async (id) => {
    setCancellingRefund(true);
    try {
      await api.post(`/appointments/${id}/refund`);
      toast.success(i18n.language === 'tr' ? 'Randevu iptal edildi ve iade başlatıldı' : 'Cancelled and refund initiated');
      setCancelRefundDialog(null); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || (i18n.language === 'tr' ? 'İade başarısız' : 'Refund failed')); }
    finally { setCancellingRefund(false); }
  };
  const handleCall = (phone) => window.location.href = `tel:${phone}`;
  const handleWhatsApp = (phone) => { let clean = phone.replace(/\D/g, ""); if (clean.startsWith("0")) clean = clean.substring(1); if (!clean.startsWith("90")) clean = "90" + clean; window.open(`https://wa.me/${clean}`, "_blank"); };

  // --- TURU BİTİRME HANDLER'I ---
  const handleTourFinish = () => {
    if (dashboardTourStorageKey) {
      localStorage.setItem(dashboardTourStorageKey, 'true');
    }
    setRunTour(false);
  };

  // --- TUR ADIMLARI TANIMLAMASI ---
  const tourSteps = [
    {
      target: 'body',
      content: t('tour.welcome', { name: currentUserName ? `, ${currentUserName}` : '' }),
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '.tour-greeting',
      content: t('tour.greeting'),
    },
    {
      target: '.tour-stats',
      content: t('tour.stats'),
    },
    {
      target: '.tour-new-appointment',
      content: t('tour.newAppointment'),
    },
    ...(userRole === 'admin' ? [{
      target: '.tour-settings',
      content: t('tour.settings'),
    }] : []),
    ...(userRole === 'staff' ? [{
      target: '.tour-breaks',
      content: t('tour.breaks'),
    }] : []),
    {
      target: '.tour-today-flow',
      content: t('tour.todayFlow'),
    },
    {
      target: '.tour-upcoming',
      content: t('tour.upcoming')
    }
  ];

  // --- DATA FILTERING ---
  const todayAppointments = appointments.filter(apt => {
    const aptDate = apt.appointment_date || apt.date;
    if (!aptDate || aptDate !== today) return false;
    if (userRole === 'staff' && !canViewAll && currentStaffUsername && apt.staff_member_id !== currentStaffUsername) return false;
    return true;
  });

  const filteredToday = todayAppointments.filter(apt => staffFilter === "all" || apt.staff_member_id === staffFilter)
    .sort((a, b) => a.appointment_time.localeCompare(b.appointment_time));

  const morningAppointments = filteredToday.filter(apt => apt.appointment_time < "12:00");
  const afternoonAppointments = filteredToday.filter(apt => apt.appointment_time >= "12:00");

  const shouldSplit = morningAppointments.length > 0 && afternoonAppointments.length > 0;

  const isCancelledStatus = (s) => s === 'İptal' || s === 'İptal Edildi' || s === 'Cancelled';

  const bookingLink = useMemo(() => {
    if (!settings?.slug) return null;
    const clean = (settings.support_phone || "").replace(/\s/g, "");
    const domain = (clean.startsWith('+44') || clean.startsWith('44')) ? 'plannapp.co.uk' : 'plannapp.co';
    return `https://${domain}/${settings.slug}`;
  }, [settings]);

  // Android WebView'da Web Share API yok → panoya kopyala
  const handleShareBookingLink = async () => {
    if (!bookingLink) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: settings?.company_name || undefined, url: bookingLink });
        return;
      } catch (e) {
        if (e?.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(bookingLink);
      toast.success(t('dashboard.todayFlow.linkCopied'));
    } catch (e) {
      toast.error(t('dashboard.todayFlow.linkCopyError'));
    }
  };

  const filteredTomorrow = appointments.filter(apt => {
    const aptDate = apt.appointment_date || apt.date;
    if (!aptDate || aptDate !== tomorrow) return false;
    if (isCancelledStatus(apt.status)) return false;
    if (userRole === 'staff' && !canViewAll && currentStaffUsername && apt.staff_member_id !== currentStaffUsername) return false;
    return true;
  }).filter(apt => staffFilter === "all" || apt.staff_member_id === staffFilter);
  const upcoming = appointments.filter(apt => {
    const aptDate = apt.appointment_date || apt.date;
    if (!aptDate || aptDate <= tomorrow) return false;
    if (isCancelledStatus(apt.status)) return false;
    if (userRole === 'staff' && !canViewAll && currentStaffUsername && apt.staff_member_id !== currentStaffUsername) return false;
    return true;
  }).filter(apt => staffFilter === "all" || apt.staff_member_id === staffFilter).sort((a, b) => {
    const d = (a.appointment_date || a.date).localeCompare(b.appointment_date || b.date);
    return d !== 0 ? d : (a.appointment_time || "").localeCompare(b.appointment_time || "");
  });

  const visibleUpcoming = upcoming.slice(0, upcomingVisibleCount);

  // Gelecek randevuları tarihe göre grupla (başlık + altında kartlar)
  const upcomingByDate = useMemo(() => {
    const groups = [];
    const idxByDate = new Map();
    for (const apt of visibleUpcoming) {
      const key = apt.appointment_date || apt.date;
      if (!idxByDate.has(key)) {
        idxByDate.set(key, groups.length);
        groups.push({ date: key, items: [] });
      }
      groups[idxByDate.get(key)].items.push(apt);
    }
    return groups;
  }, [visibleUpcoming]);

  // İsim çözümlemesi staffDirectory (admin + tüm staff, settings'ten bağımsız)
  // üzerinden yapılır → tekli/çoklu randevuda isim tutarlı görünür, titremez.
  const getStaffName = useCallback((id) => {
    if (!id) return null;
    const staff = staffDirectory.find(s => s.username === id);
    if (staff) return staff.full_name || staff.username;
    return null;
  }, [staffDirectory]);

  const customerDisplayName = useCallback((apt) => {
    const name = (apt.customer_name || "").trim();
    return name || t("dashboard.upcomingAppointments.unnamedCustomer");
  }, [t]);

  const displayCount = (userRole === 'admin' || canViewAll) ? stats?.today_appointments || 0 : todayAppointments.length;
  const displayRevenue = (userRole === 'admin' || canViewAll)
    ? stats?.bugunku_toplam_hizmet_tutari || 0
    : personnelStats?.total_revenue_generated || 0;

  const greeting = getTimeBasedGreeting();
  const currencySymbol = ((settings?.support_phone || '').replace(/\s/g, '').startsWith('+44') || (settings?.support_phone || '').replace(/\s/g, '').startsWith('44')) ? '£' : '₺';

  // Aktif (henüz tamamlanmamış) çoklu seans paketi var mı?
  // Varsa header'da küçük "Takvim" FAB'i göster — yoksa hiç gösterme.
  const hasActiveSessions = useMemo(() => {
    if (!Array.isArray(appointments)) return false;
    const todayIso = today;
    return appointments.some((a) => {
      const total = Number(a?.session_total ?? 0);
      if (total <= 1) return false;
      const date = a?.appointment_date || a?.date;
      if (!date || date < todayIso) return false;
      const status = String(a?.status || '').toLowerCase();
      const terminal = ['tamamlandı', 'tamamlandi', 'completed', 'iptal', 'iptal edildi', 'cancelled', 'canceled'];
      return !terminal.includes(status);
    });
  }, [appointments, today]);

  const appointmentListClass = "[&>*:last-child_.apt-divider]:hidden";

  // Liste satırı (iOS Takvim tarzı): bugün/yarın ve gelecek randevular aynı satırı kullanır.
  const renderAppointmentRow = (apt, { name, barClass, isCancelled, isCompleted, isExpanded, hasNote, hasMulti, menuItems }) => {
    const staffName = (userRole === 'admin' || canViewAll) ? getStaffName(apt.staff_member_id) : null;
    return (
      <div className={`relative bg-white py-3 font-appointment ${isCancelled ? 'opacity-50' : ''} ${(hasNote || hasMulti) ? 'cursor-pointer' : ''}`}>
        <div className="flex gap-3">
          <span className={`w-1 rounded-full shrink-0 ${barClass}`} />
          <div className="w-14 shrink-0 self-center flex flex-col items-center tabular-nums">
            <p className="text-[18px] leading-6 font-semibold text-zinc-900">{apt.appointment_time}</p>
            <p className="text-[16px] leading-5 text-zinc-500">{calculateEndTime(apt.appointment_time, apt.service_duration)}</p>
            {hasMulti && (
              <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-zinc-700 bg-zinc-100 rounded-full px-1.5 py-0.5">
                <Layers className="w-3 h-3" />
                <span>{apt.services.length}</span>
                <ChevronDown className={`w-3 h-3 text-zinc-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
              </span>
            )}
          </div>
          <span className="w-px shrink-0 bg-zinc-200" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <p className="truncate min-w-0 text-[17px] text-zinc-900">{name}</p>
              {hasNote && !isExpanded && <FileText className="w-3.5 h-3.5 shrink-0 text-amber-500" />}
              <SessionBadge number={apt.session_number} total={apt.session_total} />
              {SHOW_APPOINTMENT_CARD_STATUS && (
                isCompleted ? <Check className="w-4 h-4 shrink-0 text-green-600" />
                  : isCancelled ? <X className="w-4 h-4 shrink-0 text-red-600" />
                  : <Clock className="w-4 h-4 shrink-0 text-orange-500" />
              )}
              <div className="ml-auto pl-2 -my-1 shrink-0">
                <ScrollSafeDropdown
                  trigger={
                    <button type="button" className="w-8 h-8 rounded-full text-zinc-400 flex items-center justify-center hover:bg-zinc-100 active:scale-95 transition-all">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  }
                >
                  {menuItems}
                </ScrollSafeDropdown>
              </div>
            </div>
            <p className="truncate min-w-0 mt-0.5 text-[16px] text-zinc-700">{serviceDisplayName(apt)}</p>
            <div className="flex items-center gap-2 min-w-0 mt-2">
              <div className="flex items-center gap-1.5 shrink-0">
                <button type="button" onClick={(e) => { e.stopPropagation(); handleCall(apt.phone); }} className="w-8 h-8 rounded-full bg-zinc-100 text-zinc-600 flex items-center justify-center hover:bg-zinc-200 active:scale-95 transition-all"><Phone className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={(e) => { e.stopPropagation(); handleWhatsApp(apt.phone); }} className="w-8 h-8 rounded-full bg-zinc-100 text-zinc-600 flex items-center justify-center hover:bg-zinc-200 active:scale-95 transition-all"><WhatsAppIcon className="w-3.5 h-3.5" /></button>
              </div>
              {staffName && (
                <p className="ml-auto flex items-center justify-end gap-1 min-w-0 text-[14px] leading-5 text-zinc-500">
                  <User className="w-3.5 h-3.5 shrink-0 text-zinc-400" />
                  <span className="truncate">{staffName}</span>
                </p>
              )}
            </div>
          </div>
        </div>
        {isExpanded && (hasMulti || hasNote) && (
          <div className="mt-3 ml-4 space-y-2 animate-in slide-in-from-top-1 fade-in duration-200">
            {hasMulti && (
              <div className="bg-zinc-50 p-3 rounded-lg min-w-0">
                {renderServiceBreakdown(apt)}
              </div>
            )}
            {hasNote && (
              <div className="flex items-start gap-2 bg-amber-50 p-3 rounded-lg text-amber-900 text-sm min-w-0">
                <FileText className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                <p className="font-medium break-words leading-relaxed min-w-0">{apt.notes}</p>
              </div>
            )}
          </div>
        )}
        <span className="apt-divider absolute bottom-0 right-0 left-4 h-px bg-zinc-200" />
      </div>
    );
  };

  const renderAppointmentCard = (apt) => {
    const isCancelled = apt.status === "İptal" || apt.status === "İptal Edildi" || apt.status === "Cancelled" || apt.status === t('dashboard.status.cancelled');
    const isCompleted = apt.status === "Tamamlandı" || apt.status === t('dashboard.status.completed');
    const hasNote = apt.notes && apt.notes.trim().length > 0;
    const hasMulti = isMultiService(apt);
    const isExpanded = expandedNoteId === apt.id;

    const swipeIsOpen = openSwipeCardId === apt.id ? openSwipeCardSide : null;
    const handleSwipeOpenChange = (next) => {
      if (next === null) {
        if (openSwipeCardId === apt.id) {
          setOpenSwipeCardId(null);
          setOpenSwipeCardSide(null);
        }
      } else {
        setOpenSwipeCardId(apt.id);
        setOpenSwipeCardSide(next);
      }
    };

    const handleSwipeEdit = () => onEditAppointment(apt);
    const handleSwipeCancel = () => {
      if (['paid', 'deposit_paid'].includes(apt.payment_status)) {
        setCancelRefundDialog(apt);
      } else {
        handleStatusChange(apt.id, t('dashboard.status.cancelled'));
      }
    };
    const handleSwipeDelete = () => setDeleteDialog(apt);
    const handleCardTap = () => { if (hasNote || hasMulti) toggleNote(apt.id); };

    const isPending = apt.status === "Bekliyor" || apt.status === t('dashboard.status.pending');
    const aptDate = apt.appointment_date || apt.date;
    const isDue = isPending && aptDate && apt.appointment_time && `${aptDate} ${apt.appointment_time}` <= nowMinute;
    const statusBar = isPending ? (isDue ? 'bg-amber-400 motion-safe:animate-breathe' : 'bg-amber-400')
      : isCompleted ? 'bg-green-500'
      : isCancelled ? 'bg-red-500'
      : 'bg-gray-300';
    const cardInner = renderAppointmentRow(apt, {
      name: apt.customer_name,
      barClass: statusBar,
      isCancelled, isCompleted, isExpanded, hasNote, hasMulti,
      menuItems: (
        <>
          {!isCancelled && !isCompleted && (
            ['paid', 'deposit_paid'].includes(apt.payment_status)
              ? <DropdownMenuItem onClick={() => setCancelRefundDialog(apt)} className="text-red-600"><X className="w-4 h-4 mr-2"/> {t('common.cancel')}</DropdownMenuItem>
              : <DropdownMenuItem onClick={() => handleStatusChange(apt.id, t('dashboard.status.cancelled'))} className="text-red-600"><X className="w-4 h-4 mr-2"/> {t('common.cancel')}</DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => onEditAppointment(apt)}><Edit className="w-4 h-4 mr-2"/> {t('common.edit')}</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setDeleteDialog(apt)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2"/> {t('common.delete')}</DropdownMenuItem>
        </>
      ),
    });

    return (
      <SwipeableAppointmentCard
        key={apt.id}
        disabled={isCancelled || isCompleted}
        isOpen={swipeIsOpen}
        onOpenChange={handleSwipeOpenChange}
        onEdit={handleSwipeEdit}
        onCancel={handleSwipeCancel}
        onDelete={handleSwipeDelete}
        onTap={handleCardTap}
      >
        {cardInner}
      </SwipeableAppointmentCard>
    );
  };

  return (
    <div className="min-h-screen bg-white pb-24 font-sans selection:bg-gray-200">
      
      {/* --- TUR BİLEŞENİ --- */}
      <TourGuide run={runTour} steps={tourSteps} onFinish={handleTourFinish} />

      {/* 1. HEADER — Karşılama (scroll ile kayar) */}
      <div className="px-5 pt-8 pb-4 bg-white relative">

        {/* Sağ üst köşe — AI Asistan (üst) + Takvim FAB (alt, aktif seans varsa) */}
        <div className="absolute top-4 right-4 flex flex-col items-end gap-2 z-20">
          {onOpenChat && (
            <button
              onClick={onOpenChat}
              className="flex items-center gap-2 px-3 py-2 md:px-4 md:py-3 rounded-2xl bg-white/80 backdrop-blur-xl border border-gray-200/70 shadow-md shadow-black/8 hover:shadow-lg hover:bg-white active:scale-95 transition-all duration-200 group"
            >
              <div className="w-6 h-6 md:w-9 md:h-9 rounded-lg md:rounded-xl bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center shrink-0">
                <span className="text-white font-black text-[10px] md:text-[13px] leading-none tracking-tight group-hover:scale-110 transition-transform duration-200">Aİ</span>
              </div>
              <div className="text-left hidden sm:block">
                <p className="text-[11px] md:text-sm font-bold text-gray-800 leading-none">AI Asistan</p>
                <p className="text-[9px] md:text-[11px] text-gray-400 leading-none mt-0.5">PLANN</p>
              </div>
            </button>
          )}
          {hasActiveSessions && (
            <button
              type="button"
              onClick={() => onNavigate("calendar")}
              aria-label={t('dashboard.openCalendar')}
              className="flex items-center gap-2 px-3 py-2 md:px-4 md:py-3 rounded-2xl bg-white/80 backdrop-blur-xl border border-gray-200/70 shadow-md shadow-black/8 hover:shadow-lg hover:bg-white active:scale-95 transition-all duration-200 group"
            >
              <div className="w-6 h-6 md:w-9 md:h-9 rounded-lg md:rounded-xl bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center shrink-0">
                <Calendar className="w-3 h-3 md:w-4 md:h-4 text-white" />
              </div>
              <div className="text-left hidden sm:block">
                <p className="text-[11px] md:text-sm font-bold text-gray-800 leading-none">
                  {t('dashboard.openCalendar')}
                </p>
              </div>
            </button>
          )}
        </div>

        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          {/* Sol Taraf: Karşılama
              Mobilde sağ üstteki absolute butonların (AI + Takvim dikey stack)
              altına binmeyi engelle. Dikey dizilimde yatay genişlik tek buton
              kadar — pr-20 yeterli. */}
          <div
            className={`tour-greeting min-w-0 flex-1 ${
              (hasActiveSessions || onOpenChat) ? 'pr-20 sm:pr-0' : ''
            }`}
          >
            {/* Tek satır garantisi: müşteri adı uzunsa truncate (alt satıra
                düşmek yerine "...") — selamlama tam görünür kalır, emoji ismin sonunda. */}
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-x-2 min-w-0">
              <span className="whitespace-nowrap shrink-0">{greeting.text}</span>
              <span className="text-gray-900 truncate min-w-0">{currentUserName}</span>
              <span className="shrink-0 align-middle" style={{ fontSize: '1.2em' }}>{greeting.emoji}</span>
            </h1>
            <p className="text-base md:text-lg text-gray-600 font-medium mt-1.5 leading-relaxed">
              {format(new Date(), "d MMMM EEEE", { locale: dateLocale })}
            </p>
          </div>

          {/* Sağ Taraf: PERSONEL MOLA ALANI (sadece staff için) */}
          {userRole === 'staff' && (
            <div className="mt-4 md:mt-0 w-full md:w-auto md:min-w-[280px] md:mr-16 tour-breaks"> {/* CLASS EKLENDI */}
              <div className="bg-white border border-orange-100 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="bg-orange-50 p-2 rounded-lg text-orange-600"><Coffee className="w-4 h-4" /></div>
                    <span className="text-sm font-bold text-gray-900">{t('dashboard.breaks.myBreaks')}</span>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setShowBreakDialog(true)} className="h-7 text-xs text-orange-600 hover:bg-orange-50 hover:text-orange-700 font-medium transition-all hover:scale-105 active:scale-95">+ {t('common.add')}</Button>
                </div>
                {todayBreaks.length > 0 ? (
                  <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide py-2 px-1">
                    {todayBreaks.map((brk) => (
                      <div key={brk.id} className="flex items-center gap-1.5 bg-orange-50 px-2 py-1 rounded-md shrink-0 border border-orange-100 shadow-sm">
                        <span className="text-xs font-bold text-orange-800">{brk.start_time}-{brk.end_time}</span>
                        <button onClick={() => handleDeleteBreak(brk.id)} className="text-orange-400 hover:text-red-500 transition-all hover:scale-105 active:scale-95"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">{t('dashboard.breaks.noBreaksYet')}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 1b. İSTATİSTİK KARTLARI — Sticky (scroll'da sabit kalır).
          Top: safe-area-inset-top (status bar varsa altında yapışır, yoksa 0).
          Eski "+ 45px" hatalı bir offset'ti; sticky'nin görsel olarak "kayar"
          gibi gözükmesinin sebebi buydu. z-30 ile altta kalan içeriği örter. */}
      <div className="sticky z-30 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm px-5 py-2" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
        <div className="grid grid-cols-2 gap-2.5 tour-stats"> {/* CLASS EKLENDI */}
          {/* Randevu Sayısı */}
          <div className="bg-white rounded-lg p-2.5 border border-gray-200 hover:border-gray-300 transition-all cursor-default">
            <div className="flex items-center gap-1.5 mb-0.5">
              <CalendarDays className="w-3 h-3 text-gray-400" />
              <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('dashboard.summary.todayAppointments')}</span>
            </div>
            <p className="text-xl font-bold text-gray-900">{displayCount}</p>
          </div>

          {/* Ciro */}
          <div className="bg-white rounded-lg p-2.5 border border-gray-200 hover:border-gray-300 transition-all cursor-default">
            <div className="flex items-center gap-1.5 mb-0.5">
              <TrendingUp className="w-3 h-3 text-gray-400" />
              <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('dashboard.summary.todayRevenue')}</span>
            </div>
            <p className="text-xl font-bold text-gray-900">{i18n.language === 'en' ? `${currencySymbol}${Math.round(displayRevenue || 0).toLocaleString('en-GB')}` : `${(displayRevenue || 0).toLocaleString('tr-TR')}${currencySymbol}`}</p>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 space-y-6">
        {/* 2. STAFF FILTER */}
        {(userRole === 'admin' || canViewAll) && staffMembers.length > 0 && (
          <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide py-3 px-1">
            <button onClick={() => setStaffFilter("all")} className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all border shadow-sm hover:shadow-md hover:scale-105 active:scale-95 ${staffFilter === "all" ? "bg-black text-white border-black" : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"}`}>{t('dashboard.todayFlow.allStaff')}</button>
            {staffMembers.map((staff) => (
              <button key={staff.username} onClick={() => setStaffFilter(staff.username)} className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all flex items-center gap-2 border shadow-sm hover:shadow-md hover:scale-105 active:scale-95 ${staffFilter === staff.username ? "bg-black text-white border-black" : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"}`}>
                {staffFilter === staff.username && <User className="w-3.5 h-3.5" />} {staff.full_name}
              </button>
            ))}
          </div>
        )}

        {/* 3. BUGÜNÜN AKIŞI */}
        <div className="tour-today-flow"> {/* CLASS EKLENDI */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base md:text-lg font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2">
              {t('dashboard.todayFlow.title')}
            </h2>
            <span className="text-xs md:text-sm font-bold bg-zinc-100 text-zinc-500 px-3 py-1 rounded-full shadow-sm">{filteredToday.length} {t('common.appointments')}</span>
          </div>

          {filteredToday.length === 0 ? (
            <div className="flex flex-col items-center text-center pt-6 pb-8 px-4">
              <div className="w-14 h-14 rounded-full bg-zinc-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-zinc-500" strokeWidth={1.8} />
              </div>
              <p className="mt-4 text-[17px] font-semibold text-zinc-900">{t('dashboard.todayFlow.emptyTitle')}</p>
              {bookingLink && (
                <p className="mt-1 max-w-sm text-[15px] leading-snug text-zinc-500">{t('dashboard.todayFlow.emptySubtitle')}</p>
              )}
              <button
                type="button"
                onClick={() => onNewAppointment()}
                className="mt-6 w-full max-w-sm h-12 rounded-xl bg-zinc-900 hover:bg-black text-white text-[15px] font-bold shadow-lg flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
              >
                <Plus className="w-4 h-4" strokeWidth={2.5} />
                {t('dashboard.todayFlow.addAppointment')}
              </button>
              {bookingLink && (
                <>
                  <button
                    type="button"
                    onClick={handleShareBookingLink}
                    className="mt-3 w-full max-w-sm h-12 rounded-xl bg-white border border-zinc-200 text-zinc-900 text-[15px] font-semibold flex items-center justify-center gap-2 hover:bg-zinc-50 active:scale-[0.98] transition-all"
                  >
                    <Share className="w-4 h-4" />
                    {t('dashboard.todayFlow.shareBookingLink')}
                  </button>
                  <p className="mt-3 text-[13px] text-zinc-400 break-all">{bookingLink.replace(/^https?:\/\//, "")}</p>
                </>
              )}
            </div>
          ) : (
            shouldSplit ? (
              <>
                {/* Mobil/App: öğleden önce-sonra ayrımı gizli, tek liste */}
                <div className={`${appointmentListClass} md:hidden`}>
                  {filteredToday.map(apt => renderAppointmentCard(apt))}
                </div>
                {/* Masaüstü (web): öğleden önce / öğleden sonra 2 sütun */}
                <div className="hidden md:grid md:grid-cols-2 md:gap-6">
                  <div>
                    <div className="flex items-center gap-2 text-sm font-bold text-orange-600 uppercase tracking-wider mb-4"><Sun className="w-4 h-4" /> {t('dashboard.todayFlow.beforeNoon')}</div>
                    {morningAppointments.length > 0 ? <div className={appointmentListClass}>{morningAppointments.map(apt => renderAppointmentCard(apt))}</div> : <div className="p-4 text-center bg-gray-50 rounded-xl text-gray-400 text-xs italic shadow-sm">{t('dashboard.todayFlow.noAppointmentsShort')}</div>}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-sm font-bold text-blue-600 uppercase tracking-wider mb-4"><Moon className="w-4 h-4" /> {t('dashboard.todayFlow.afterNoon')}</div>
                    {afternoonAppointments.length > 0 ? <div className={appointmentListClass}>{afternoonAppointments.map(apt => renderAppointmentCard(apt))}</div> : <div className="p-4 text-center bg-gray-50 rounded-xl text-gray-400 text-xs italic shadow-sm">{t('dashboard.todayFlow.noAppointmentsShort')}</div>}
                  </div>
                </div>
              </>
            ) : (
              <div className={appointmentListClass}>
                {filteredToday.map(apt => renderAppointmentCard(apt))}
              </div>
            )
          )}
        </div>

        {/* 4. YARININ RANDEVULARI */}
        {filteredTomorrow.length > 0 && (
          <>
            {filteredToday.length > 0 && (
              <div className="my-6" />
            )}
            
            <div className="tour-tomorrow">
              <div className={`flex items-center justify-between mb-4 ${filteredToday.length === 0 ? 'mt-8' : ''}`}>
              <h2 className="text-base md:text-lg font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2">{t('dashboard.tomorrowAppointments.title')}</h2>
              <span className="text-xs md:text-sm font-bold bg-zinc-100 text-zinc-500 px-3 py-1 rounded-full shadow-sm">{filteredTomorrow.length} {t('common.appointments')}</span>
            </div>
            {(() => {
              const tomorrowMorning = filteredTomorrow.filter(apt => apt.appointment_time < "12:00");
              const tomorrowAfternoon = filteredTomorrow.filter(apt => apt.appointment_time >= "12:00");
              const tomorrowShouldSplit = tomorrowMorning.length > 0 && tomorrowAfternoon.length > 0;
              return (
                <>
                  {/* Mobil: her zaman tek liste */}
                  <div className={`${appointmentListClass} md:hidden`}>
                    {filteredTomorrow.map(apt => renderAppointmentCard(apt))}
                  </div>
                  {/* Masaüstü: split varsa 2 sütun */}
                  <div className="hidden md:block">
                    {tomorrowShouldSplit ? (
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <div className="flex items-center gap-2 text-sm font-bold text-orange-600 uppercase tracking-wider mb-4"><Sun className="w-4 h-4" /> {t('dashboard.todayFlow.beforeNoon')}</div>
                          <div className={appointmentListClass}>{tomorrowMorning.map(apt => renderAppointmentCard(apt))}</div>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 text-sm font-bold text-blue-600 uppercase tracking-wider mb-4"><Moon className="w-4 h-4" /> {t('dashboard.todayFlow.afterNoon')}</div>
                          <div className={appointmentListClass}>{tomorrowAfternoon.map(apt => renderAppointmentCard(apt))}</div>
                        </div>
                      </div>
                    ) : (
                      <div className={appointmentListClass}>
                        {filteredTomorrow.map(apt => renderAppointmentCard(apt))}
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
          </>
        )}

        {/* 5. GELECEK RANDEVULAR — tarihe göre gruplu, lila vurgulu, mobil uyumlu */}
        {upcoming.length > 0 && (
          <div className="tour-upcoming mt-8">
            <h2 className="text-base md:text-lg font-bold text-gray-900 mb-4 flex items-center gap-2 uppercase tracking-wider">{t('dashboard.upcomingAppointments.title')}</h2>
            <div className="space-y-6">
              {upcomingByDate.map((group) => (
                <div key={group.date}>
                  <div className="flex items-center justify-between gap-2 mb-3 px-0.5">
                    <span className="text-[15px] font-bold text-gray-900 capitalize">
                      {format(new Date(group.date), "d MMMM EEEE", { locale: dateLocale })}
                    </span>
                    <span className="text-xs md:text-sm font-bold bg-zinc-100 text-zinc-500 px-3 py-1 rounded-full shadow-sm whitespace-nowrap">{group.items.length} {t('common.appointments')}</span>
                  </div>
                  <div className={appointmentListClass}>
                    {group.items.map((apt) => (
                      (() => {
                        const hasNote = apt.notes && apt.notes.trim().length > 0;
                        const hasMulti = isMultiService(apt);
                        const isExpanded = expandedNoteId === apt.id;
                        const isCancelledU = apt.status === "İptal" || apt.status === "İptal Edildi" || apt.status === "Cancelled" || apt.status === t('dashboard.status.cancelled');
                        const isCompletedU = apt.status === "Tamamlandı" || apt.status === t('dashboard.status.completed');
                        const swipeIsOpenU = openSwipeCardId === apt.id ? openSwipeCardSide : null;
                        const handleSwipeOpenChangeU = (next) => {
                          if (next === null) {
                            if (openSwipeCardId === apt.id) {
                              setOpenSwipeCardId(null);
                              setOpenSwipeCardSide(null);
                            }
                          } else {
                            setOpenSwipeCardId(apt.id);
                            setOpenSwipeCardSide(next);
                          }
                        };
                        const handleSwipeCancelU = () => {
                          if (['paid', 'deposit_paid'].includes(apt.payment_status)) {
                            setCancelRefundDialog(apt);
                          } else {
                            handleStatusChange(apt.id, t('dashboard.status.cancelled'));
                          }
                        };
                        const upcomingInner = renderAppointmentRow(apt, {
                          name: customerDisplayName(apt),
                          barClass: 'bg-indigo-600',
                          isCancelled: isCancelledU, isCompleted: isCompletedU, isExpanded, hasNote, hasMulti,
                          menuItems: (
                            <>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEditAppointment(apt); }}>
                                <Edit className="w-4 h-4 mr-2" /> {t('common.edit')}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setDeleteDialog(apt); }} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" /> {t('common.delete')}
                              </DropdownMenuItem>
                            </>
                          ),
                        });
                        return (
                          <SwipeableAppointmentCard
                            key={apt.id}
                            disabled={isCancelledU || isCompletedU}
                            isOpen={swipeIsOpenU}
                            onOpenChange={handleSwipeOpenChangeU}
                            onEdit={() => onEditAppointment(apt)}
                            onCancel={handleSwipeCancelU}
                            onDelete={() => setDeleteDialog(apt)}
                            onTap={() => { if (hasNote || hasMulti) toggleNote(apt.id); }}
                          >
                            {upcomingInner}
                          </SwipeableAppointmentCard>
                        );
                      })()
                    ))}
                  </div>
                </div>
              ))}
              {upcoming.length > upcomingVisibleCount && (
                <button
                  onClick={() => setUpcomingVisibleCount(prev => prev + 10)}
                  className="p-3 text-center bg-gray-50 hover:bg-gray-100 text-xs md:text-sm font-medium text-gray-500 rounded-xl border border-gray-200 transition-colors cursor-pointer w-full"
                >
                  + {upcoming.length - upcomingVisibleCount} {t('common.more')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <AlertDialogContent className="rounded-2xl max-w-xs shadow-lg">
          <AlertDialogHeader><AlertDialogTitle>{t('dashboard.deleteDialog.title')}</AlertDialogTitle><AlertDialogDescription>{t('dashboard.deleteDialog.description', { name: deleteDialog?.customer_name })}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-xl transition-all hover:scale-105 active:scale-95">{t('dashboard.deleteDialog.cancel')}</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(deleteDialog?.id)} className="bg-red-600 hover:bg-red-700 rounded-xl transition-all hover:scale-105 active:scale-95">{t('dashboard.deleteDialog.delete')}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel + Refund Dialog */}
      <AlertDialog open={!!cancelRefundDialog} onOpenChange={() => setCancelRefundDialog(null)}>
        <AlertDialogContent className="rounded-2xl max-w-sm shadow-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-black text-zinc-900">
              {i18n.language === 'tr' ? 'Randevu İptal' : 'Cancel Appointment'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600 font-medium">
              {cancelRefundDialog?.customer_name} — {cancelRefundDialog?.service_name}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2.5">
            <button type="button" disabled={cancellingRefund}
              onClick={() => handleCancelOnly(cancelRefundDialog?.id)}
              className="w-full p-3 rounded-xl border-2 border-zinc-200 hover:border-zinc-400 text-left transition-all disabled:opacity-50">
              <p className="font-bold text-zinc-900 text-sm">{i18n.language === 'tr' ? 'Sadece İptal Et' : 'Cancel Only'}</p>
              <p className="text-xs text-zinc-500 mt-0.5">{i18n.language === 'tr' ? 'Randevu iptal edilir, iade yapılmaz' : 'Cancel without refund'}</p>
            </button>
            <button type="button" disabled={cancellingRefund}
              onClick={() => handleCancelAndRefund(cancelRefundDialog?.id)}
              className="w-full p-3 rounded-xl border-2 border-red-200 hover:border-red-400 text-left transition-all disabled:opacity-50">
              <p className="font-bold text-red-700 text-sm">{i18n.language === 'tr' ? 'İptal Et ve İade Yap' : 'Cancel & Refund'}</p>
              <p className="text-xs text-red-400 mt-0.5">{i18n.language === 'tr' ? 'Randevu iptal edilir ve ödeme iade edilir' : 'Cancel and refund the payment'}</p>
            </button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancellingRefund} className="rounded-xl font-bold">{t('common.cancel')}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Seans Planlama Dialog */}
      <SessionPlannerDialog
        open={showSessionPlanner}
        onOpenChange={setShowSessionPlanner}
        appointment={sessionPlannerAppointment}
        onSuccess={onRefresh}
      />

      {showBreakDialog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <Card className="w-full max-w-xs rounded-2xl shadow-2xl p-5">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{t('dashboard.breaks.addBreakTitle')}</h3>
            <div className="grid grid-cols-2 gap-3 mb-5">
              <div><label className="text-xs font-bold text-gray-500 mb-1 block">{t('common.start')}</label><input type="time" value={newBreakStart} onChange={e => setNewBreakStart(e.target.value)} className="w-full bg-gray-50 border-gray-200 rounded-lg p-2 text-sm font-bold text-gray-900 focus:ring-black shadow-sm" /></div>
              <div><label className="text-xs font-bold text-gray-500 mb-1 block">{t('common.end')}</label><input type="time" value={newBreakEnd} onChange={e => setNewBreakEnd(e.target.value)} className="w-full bg-gray-50 border-gray-200 rounded-lg p-2 text-sm font-bold text-gray-900 focus:ring-black shadow-sm" /></div>
            </div>
            <div className="flex gap-2"><Button variant="outline" onClick={() => setShowBreakDialog(false)} className="flex-1 rounded-lg h-10 text-xs transition-all hover:scale-105 active:scale-95">{t('common.cancel')}</Button><Button onClick={handleAddBreak} disabled={addingBreak} className="flex-1 rounded-lg h-10 bg-black hover:bg-gray-800 text-white text-xs transition-all hover:scale-105 active:scale-95">{addingBreak ? "..." : t('common.add')}</Button></div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Dashboard;